import * as THREE from 'three';

import { PARTICLE_FRAG, PARTICLE_VERT, POSITION_FRAG, VELOCITY_FRAG } from './orbShaders';
import { tintColor } from './tint';

/**
 * 오브 입자 — 원본 `LandingOrbParticles` / `LandingOrbFlareParticles` 의 이식.
 *
 * <h2>GPGPU 다</h2>
 * 좌표를 CPU 가 아니라 <b>텍스처</b>에 담는다. 128×128 한 장이 곧 16384 개의 좌표고,
 * 매 프레임 그 텍스처를 셰이더로 한 번 그려서 다음 프레임 좌표를 만든다(핑퐁).
 * 속도도 같은 방식이라 판이 넷이다.
 *
 * <h2>입자가 둘로 갈린다</h2>
 * `selected = step(uShapeThreshold, rand)` 라 **3/4 이 `selected`** 다 — 구 껍질
 * (`uRadius`)에 붙어 컬 노이즈를 타고 흐른다. 나머지 1/4 은 붙들리지 않고 바깥으로
 * 흘러 나간다. 섬광 판이 보여 주는 것이 이 1/4 이다.
 *
 * <p>uniform 은 전부 원본 씬 파일 실측값이다.
 */

/* 원본은 «재질 기본값» 위에 씬 파일 값을 덮어쓴다. 본체는 거의 전부 덮어쓰지만
   섬광은 셋만 덮어쓴다 — 나머지는 기본값이다. 이 구분을 놓쳐서 섬광에 본체 값을
   물려줬더니 `uDecay2 .396` 으로 입자가 일찍 죽어 반지름 3 을 못 넘었고, 그래서
   구 밖으로 튀는 분출이 통째로 빠졌다. */

/**
 * `textureWidth: 256` — 256×256 = 65,536 개. <b>원본 값이자 기본값</b>이다.
 *
 * <p>{@link OrbParticlesOptions.grid} 로 낮출 수 있다 (2026-09-21). 이 수는 화면에 몇 px 로
 * 그리든 같아서, 앱 하단 바의 68dp 구슬도 웹 히어로와 똑같이 65,536 톨을 돌렸다.
 */
const FBO = 256;
/** `planeScale: .1` — 알갱이 사각형의 한 변. */
const PLANE_SCALE = 0.1;
/** `sizeRange = [.5, 1]` — 인스턴스별 aScale. */
const SIZE_RANGE: [number, number] = [0.5, 1];

/** 속도 시뮬 재질 기본값. */
const VEL_DEFAULT = {
  uFriction: 0,
  uShapeThreshold: 0.25,
  uCurlSize: 0.5,
  uCurlNoiseSpeed: 0.3,
  uCurlStrength: 0.03,
  uCurlNoisePersistence: 0.03,
  uFrequency: [15, 8] as [number, number],
  uAmplitude: [0.1, 0.1] as [number, number],
  uFluidStrength: 0.25,
};
/** 위치 시뮬 재질 기본값. */
const POS_DEFAULT = {
  uDecay: 0.01,
  uDecay2: 0.01,
  uLerpSpeed: 0.2,
  uLerpSpeed2: 0.2,
  uRadius: 2.85,
  uFluidStrength: 0.25,
};

/** `LandingOrbParticles.simulation.uniforms` — 씬 파일이 덮어쓴 것들. */
const MAIN_OVERRIDE = {
  uCurlSize: 0.2799999999999992,
  uCurlNoiseSpeed: 3.6600000000000037,
  uCurlStrength: 0.08700000000000001,
  uCurlNoisePersistence: 0.16700000000000043,
  uFrequency: [7.580000000000001, 9.64] as [number, number],
  uAmplitude: [0.1350000000000001, 0.12099999999999991] as [number, number],
  uLerpSpeed: 0.20399999999999982,
  uLerpSpeed2: 0.3840000000000002,
  uDecay: 0.37399999999999944,
  uDecay2: 0.3960000000000003,
  uRadius: 2.95,
  uFriction: 0,
  uShapeThreshold: 0.25,
  uFluidStrength: 0.1,
};

const C = (r: number, g: number, b: number) => new THREE.Color(r, g, b);


const COLORS = {
  uAmbientColor1: C(0.30980392156862746, 0.1803921568627451, 0.17254901960784313),
  uAmbientColor2: C(0.4666666666666667, 0.32941176470588235, 0.19607843137254902),
  uBaseColor: C(0.8666666666666667, 0.2980392156862745, 0.2980392156862745),
  uBaseColor2: C(1, 0.5843137254901961, 0.3215686274509804),
  uEdgeColor1: C(0.9882352941176471, 0.6078431372549019, 0.49411764705882355),
  uEdgeColor2: C(1, 0.7450980392156863, 0.5333333333333333),
  uRandomHighlightBaseColor: C(1, 0, 0),
  uBloomColor1: C(0.9529411764705882, 0.6666666666666666, 0.6352941176470588),
  uBloomColor2: C(0.9607843137254902, 0.7372549019607844, 0.6274509803921569),
  uLightColor: C(1, 0.9058823529411765, 0.8705882352941177),
  uLightColor2: C(1, 0.9137254901960784, 0.8392156862745098),
};

/** 입자 하나에 씌우는 구면 법선 스프라이트. 원본의 `tNormal` 자리. */
function normalSprite() {
  const n = 64;
  const data = new Uint8Array(n * n * 4);
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      const nx = ((x + 0.5) / n) * 2 - 1;
      const ny = ((y + 0.5) / n) * 2 - 1;
      const r2 = nx * nx + ny * ny;
      const i = (y * n + x) * 4;
      const nz = r2 < 1 ? Math.sqrt(1 - r2) : 0;
      data[i] = ((nx * 0.5 + 0.5) * 255) | 0;
      data[i + 1] = ((ny * 0.5 + 0.5) * 255) | 0;
      data[i + 2] = ((nz * 0.5 + 0.5) * 255) | 0;
      data[i + 3] = 255;
    }
  }
  const t = new THREE.DataTexture(data, n, n, THREE.RGBAFormat);
  t.needsUpdate = true;
  return t;
}

/** 초기 좌표 — 구 껍질 위에 고루. `w` 는 수명이다. */
function originTexture(grid: number) {
  const total = grid * grid;
  const data = new Float32Array(total * 4);
  for (let i = 0; i < total; i++) {
    const k = i + 0.5;
    const phi = Math.acos(1 - (2 * k) / total);
    const theta = Math.PI * (1 + Math.sqrt(5)) * k;
    data[i * 4] = Math.cos(theta) * Math.sin(phi);
    data[i * 4 + 1] = Math.sin(theta) * Math.sin(phi);
    data[i * 4 + 2] = Math.cos(phi);
    data[i * 4 + 3] = Math.random();
  }
  const t = new THREE.DataTexture(data, grid, grid, THREE.RGBAFormat, THREE.FloatType);
  t.needsUpdate = true;
  return t;
}

function pingPong(grid: number) {
  const opts = {
    type: THREE.FloatType,
    format: THREE.RGBAFormat,
    minFilter: THREE.NearestFilter,
    magFilter: THREE.NearestFilter,
    depthBuffer: false,
    stencilBuffer: false,
  } as const;
  const st = {
    read: new THREE.WebGLRenderTarget(grid, grid, opts),
    write: new THREE.WebGLRenderTarget(grid, grid, opts),
    swap() {
      const t = st.read;
      st.read = st.write;
      st.write = t;
    },
  };
  return st;
}

const SIM_VERT = 'varying vec2 vUv;void main(){vUv=uv;gl_Position=vec4(position.xy,0.0,1.0);}';

export type OrbParticlesOptions = {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  cloudsTexture: THREE.Texture;
  fluidTexture: () => THREE.Texture;
  lightPosition: THREE.Vector3;
  lightPosition2: THREE.Vector3;
  /** 주면 알갱이 팔레트를 이 색 계열로 옮긴다(`tintColor`). 없으면 원본 색 그대로. */
  tint?: THREE.Color | null;
  /**
   * 알갱이 <b>한 톨의 크기</b>(세계 단위). 안 주면 원본 값 0.35 다.
   *
   * <p>작은 판에서 올린다. 구를 100px 남짓으로 그리면 65,536 톨이 서로 겹쳐 <b>매끈한 표면</b>이
   * 되고, 밝은 2% 만 점으로 남아 «알갱이가 없는» 그림이 된다(앱 구슬 실측 2026-09-18: 캔버스
   * 94×94). 톨을 키우면 같은 개수로 웹에서 보이던 살결이 난다.
   */
  size?: number;
  /**
   * 시뮬 격자 한 변 — <b>톨 수는 이것의 제곱</b>이다. 안 주면 원본 {@link FBO}(256 · 65,536 톨).
   *
   * <p>낮추면 표면이 성겨지므로 부르는 쪽이 {@link #size} 를 같은 비율로 올려 덮는 넓이를 맞춘다.
   */
  grid?: number;
};

export function createOrbParticles(o: OrbParticlesOptions) {
  const { renderer, scene, camera } = o;
  const V = { ...VEL_DEFAULT, ...MAIN_OVERRIDE };
  const P = { ...POS_DEFAULT, ...MAIN_OVERRIDE };

  /* 2 의 거듭제곱이 아니어도 돌지만, 픽셀과 톨이 1:1 로 맞물려야 시뮬 텍스처 표집이 흐트러지지
     않으므로 정수로만 받는다. 너무 작으면 구 껍질이 눈에 띄게 듬성해져 바닥을 둔다. */
  const grid = Math.max(32, Math.round(o.grid ?? FBO));

  const origin = originTexture(grid);
  const normalTex = normalSprite();
  const position = pingPong(grid);
  const velocity = pingPong(grid);

  const simScene = new THREE.Scene();
  const simCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  const simGeo = new THREE.PlaneGeometry(2, 2);
  const simQuad = new THREE.Mesh(simGeo);
  simScene.add(simQuad);

  const shared = {
    uTime: { value: 0 },
    uDelta: { value: 16 },
    uNormalizedDelta: { value: 1 },
    uSetup: { value: 1 },
    uShapeThreshold: { value: V.uShapeThreshold },
    tFluidSim: { value: o.fluidTexture() },
    uModelMatrix: { value: new THREE.Matrix4() },
    uViewMatrix: { value: camera.matrixWorldInverse },
    uProjectionMatrix: { value: camera.projectionMatrix },
  };

  const velUniforms = {
    ...shared,
    tTexture: { value: velocity.read.texture },
    tPosition: { value: position.read.texture },
    tOriginalPosition: { value: origin },
    uFriction: { value: V.uFriction },
    uCurlSize: { value: V.uCurlSize },
    uCurlNoiseSpeed: { value: V.uCurlNoiseSpeed },
    uCurlStrength: { value: V.uCurlStrength },
    uCurlNoisePersistence: { value: V.uCurlNoisePersistence },
    uFrequency: { value: new THREE.Vector2(...V.uFrequency) },
    uAmplitude: { value: new THREE.Vector2(...V.uAmplitude) },
    uNoiseTranslation: { value: new THREE.Vector3(105.12000000000008, 104.0200000000002, 97.25999999999998) },
  };
  const velMat = new THREE.ShaderMaterial({
    vertexShader: SIM_VERT,
    fragmentShader: VELOCITY_FRAG,
    uniforms: velUniforms,
    depthTest: false,
    depthWrite: false,
  });

  const posUniforms = {
    ...shared,
    tTexture: { value: position.read.texture },
    tVelocity: { value: velocity.read.texture },
    tOriginalTexture: { value: origin },
    uRadius: { value: P.uRadius },
    uDecay: { value: P.uDecay },
    uDecay2: { value: P.uDecay2 },
    uLerpSpeed: { value: P.uLerpSpeed },
    uLerpSpeed2: { value: P.uLerpSpeed2 },
    uFluidStrength: { value: P.uFluidStrength },
  };
  const posMat = new THREE.ShaderMaterial({
    vertexShader: SIM_VERT,
    fragmentShader: POSITION_FRAG,
    uniforms: posUniforms,
    depthTest: false,
    depthWrite: false,
  });

  // ── 그리는 쪽. 사각형 하나를 grid² 개 인스턴스로.
  const count = grid * grid;
  const quad = new THREE.PlaneGeometry(PLANE_SCALE, PLANE_SCALE);
  const geo = new THREE.InstancedBufferGeometry();
  geo.index = quad.index;
  geo.attributes.position = quad.attributes.position!;
  geo.attributes.uv = quad.attributes.uv!;
  geo.attributes.normal = quad.attributes.normal!;
  geo.instanceCount = count;

  const fboUv = new Float32Array(count * 2);
  const random = new Float32Array(count * 3);
  const scale = new Float32Array(count);
  for (let i = 0; i < count; i++) {
    fboUv[i * 2] = ((i % grid) + 0.5) / grid;
    fboUv[i * 2 + 1] = (Math.floor(i / grid) + 0.5) / grid;
    random[i * 3] = Math.random();
    random[i * 3 + 1] = Math.random();
    random[i * 3 + 2] = Math.random();
    scale[i] = SIZE_RANGE[0] + Math.random() * (SIZE_RANGE[1] - SIZE_RANGE[0]);
  }
  geo.setAttribute('aFboUv', new THREE.InstancedBufferAttribute(fboUv, 2));
  geo.setAttribute('aRandom', new THREE.InstancedBufferAttribute(random, 3));
  geo.setAttribute('aScale', new THREE.InstancedBufferAttribute(scale, 1));

  const drawUniforms: Record<string, THREE.IUniform> = {
    tPosition: { value: position.read.texture },
    tVelocity: { value: velocity.read.texture },
    tClouds: { value: o.cloudsTexture },
    tNormal: { value: normalTex },
    uTime: { value: 0 },
    uRotationSpeed: { value: 0 },
    uOpacity: { value: 1 },
    uParticleSize: { value: o.size ?? 0.35 },
    uScaleRange: { value: new THREE.Vector2(0.5, 1.2) },
    uBrightColorPick: { value: new THREE.Vector2(0.13, 0.16) },
    uEdgeColorStop: { value: new THREE.Vector2(1.9299999999999962, 2.8899999999999904) },
    uRandomHighlightSelect: { value: 0.98 },
    uRandomHighlightColor: { value: new THREE.Vector3(10, 1.57, 1.3100000000000005) },
    uSunDistanceColorStep: { value: 9.929999999999692 },
    uCloudsFadeIntensity: { value: 2.800000000000005 },
    uDistanceFadeNearFar: { value: new THREE.Vector2(18.180000000000096, 19.529999999999873) },
    uShininess: { value: 83 },
    uLightIntensity: { value: 1.4900000000000004 },
    uSpecularIntensity: { value: 0.64 },
    uLightIntensity2: { value: 1 },
    uSpecularIntensity2: { value: 2 },
    uLightPosition: { value: o.lightPosition },
    uLightPosition2: { value: o.lightPosition2 },
    uDebugDistanceFade: { value: false },
    uDebugCloudsFade: { value: false },
  };
  /* 팔레트 열한 색. `tint` 가 오면 색상만 그쪽 계열로 옮기고 밝기 층은 남긴다(`tintColor`). */
  for (const [k, v] of Object.entries(COLORS)) {
    drawUniforms[k] = { value: o.tint ? tintColor(v, o.tint) : v.clone() };
  }

  const mesh = new THREE.Mesh(
    geo,
    new THREE.ShaderMaterial({
      vertexShader: PARTICLE_VERT,
      fragmentShader: PARTICLE_FRAG,
      uniforms: drawUniforms,
      transparent: false,
      depthTest: true,
      depthWrite: true,
    }),
  );
  mesh.frustumCulled = false;
  scene.add(mesh);

  const pass = (m: THREE.Material, target: THREE.WebGLRenderTarget) => {
    simQuad.material = m;
    renderer.setRenderTarget(target);
    renderer.render(simScene, simCamera);
    renderer.setRenderTarget(null);
  };

  let first = true;
  const update = (elapsed: number, deltaMs: number) => {
    shared.uTime.value = elapsed;
    shared.uDelta.value = deltaMs;
    shared.uNormalizedDelta.value = Math.min(2, deltaMs / 16.666);
    shared.uSetup.value = first ? 1 : 0;
    shared.tFluidSim.value = o.fluidTexture();
    shared.uModelMatrix.value.copy(mesh.matrixWorld);
    drawUniforms.uTime!.value = elapsed;

    velUniforms.tTexture.value = velocity.read.texture;
    velUniforms.tPosition.value = position.read.texture;
    pass(velMat, velocity.write);
    velocity.swap();

    posUniforms.tTexture.value = position.read.texture;
    posUniforms.tVelocity.value = velocity.read.texture;
    pass(posMat, position.write);
    position.swap();

    drawUniforms.tPosition!.value = position.read.texture;
    drawUniforms.tVelocity!.value = velocity.read.texture;
    first = false;
  };

  const dispose = () => {
    scene.remove(mesh);
    geo.dispose();
    quad.dispose();
    simGeo.dispose();
    (mesh.material as THREE.Material).dispose();
    velMat.dispose();
    posMat.dispose();
    position.read.dispose();
    position.write.dispose();
    velocity.read.dispose();
    velocity.write.dispose();
    origin.dispose();
    normalTex.dispose();
  };

  /**
   * 팔레트 계열을 <b>돌아가는 중에</b> 바꾼다. 화면에서 색을 고르는 자리가 이걸 쓴다 —
   * 다시 만들면 입자 65,536 개가 처음 자리로 돌아가 한 번 뭉쳤다 흩어진다.
   */
  const setTint = (tint: THREE.Color | null) => {
    for (const [k, v] of Object.entries(COLORS)) {
      (drawUniforms[k]!.value as THREE.Color).copy(tint ? tintColor(v, tint) : v);
    }
  };

  return { mesh, update, dispose, setTint };
}
